/**
 * @file Utilities for using Cloudflare Turnstile to check if a user is human
 * Reference: https://developers.cloudflare.com/turnstile/
 */
import environment from '@@/bits/environment'
import { loadJs } from '@@/bits/load'

// Don't use global turnstile outside of this file
declare global {
  interface Window {
    turnstile: Turnstile
  }
}

interface TurnstileOptions {
  // https://developers.cloudflare.com/turnstile/get-started/client-side-rendering/#configurations
  // There are more options but I only put the ones I think are useful for us here
  sitekey: string
  action?: string
  cData?: Record<string, unknown>
  callback?: (token: string) => void
  'expired-callback'?: () => void
  'timeout-callback'?: () => void
  'error-callback'?: () => void
  theme?: 'light' | 'dark' | 'auto'
  tabindex?: number
}

interface Turnstile {
  // returns widgetId (string) on successful call and undefined on failure
  render: (selectorOrHTMLElement: string | HTMLElement, options: TurnstileOptions) => string | undefined
  reset: (widgetId?: string) => void
  getResponse: (widgetId: string) => string | null | undefined
}

// Should be okay to hard code site key here since it's not a secret and needs to be available in the frontend
// For none production environment, we can test using these site keys (Reference: https://developers.cloudflare.com/turnstile/frequently-asked-questions/#are-there-sitekeys-and-secret-keys-that-can-be-used-for-testing):
//   - 1x00000000000000000000AA: always pass
//   - 2x00000000000000000000AB: always block
//   - 3x00000000000000000000FF: force interactive challenge (for non invisible widget)
const SITE_KEY = environment === 'production' ? '0x4AAAAAAACTVvAGayAtbpXb' : '1x00000000000000000000AA'

// load script through our permacache proxy
const SCRIPT_URL = 'https://rf.padletcdn.com/challenges.cloudflare.com/turnstile/v0/api.js'

interface LoadTurnstileOptions {
  explicitRendering: boolean
}
// Load Cloudflare Turnstile scripts, should only run this once for each page
async function loadTurnstile({ explicitRendering }: LoadTurnstileOptions): Promise<Turnstile> {
  await loadJs(`${SCRIPT_URL}${explicitRendering ? '?render=explicit' : ''}`)
  return window.turnstile
}

async function loadInvisibleTurnstile(options: Omit<TurnstileOptions, 'sitekey'>): Promise<string | undefined> {
  const dummyId = `cf-turnstile-dummy-${Date.now()}`
  const dummyDiv = document.createElement('div')
  dummyDiv.id = dummyId
  document.body.appendChild(dummyDiv)

  const turnstile = await loadTurnstile({ explicitRendering: true })

  const renderOptions: TurnstileOptions = { ...options, sitekey: SITE_KEY }

  // Cloudflare's token expires every 5 minutes: https://developers.cloudflare.com/turnstile/frequently-asked-questions/#how-long-is-a-turnstile-token-valid-for-before-it-is-rejected-by-siteverify
  // If no special instruction is set for when the the token expired/timeout, just try to get a new token
  if (renderOptions['expired-callback'] == null) {
    renderOptions['expired-callback'] = () => {
      turnstile.reset()
    }
  }
  if (renderOptions['timeout-callback'] == null) {
    renderOptions['timeout-callback'] = () => {
      turnstile.reset()
    }
  }

  return turnstile.render(`#${dummyId}`, renderOptions)
}

// Should only be called after turnstile is loaded to get the human verification token
// widgetId can the id selector of the parent element of turnstile or th widget id returned by turnstile.render/loadInvisibleTurnstile
function getTurnstileVerificationToken(widgetId: string | null): string | null | undefined {
  if (widgetId === null) {
    return null
  }
  return window.turnstile.getResponse(widgetId)
}

// Reset turnstile to get a new token
function resetTurnstile(widgetId: string): void {
  if (widgetId == null) {
    return
  }
  window.turnstile.reset(widgetId)
}

export { getTurnstileVerificationToken, loadInvisibleTurnstile, resetTurnstile }
